import { Job } from '../class/job.class';
import { TaskModel } from '../models/task.model';
import { WsUpdateTaskMessage } from '../models/ws-update-task-message.model';
import { StatusEnum } from '../enums/status.enum';
import { TaskEnum } from '../enums/task.enum';
import { JobModel } from '../models/job.model';

/**
 * Détermine le statut d'un job en fonction de l'état de ses tâches
 * @param tasks Liste des tâches du job
 * @returns Statut calculé du job
 */
function calculateJobStatus(tasks: TaskModel[]): StatusEnum {
  // Vérifier s'il y a des erreurs
  const hasError = tasks.some((task) => task.status === StatusEnum.ERROR);
  if (hasError) return StatusEnum.ERROR;

  // Vérifier s'il y a des tâches en attente de réponse
  const hasWaitingResponse = tasks.some(
    (task) => task.status === StatusEnum.WAITING_RESPONSE
  );
  if (hasWaitingResponse) return StatusEnum.WAITING_RESPONSE;

  // Vérifier si la dernière tâche (VIDEO_EDITING) est complétée
  const finalTask = tasks.find((task) => task.type === TaskEnum.VIDEO_EDITING);
  if (finalTask && finalTask.status === StatusEnum.COMPLETED) {
    return StatusEnum.COMPLETED;
  }

  // Vérifier s'il y a des tâches en cours
  const hasInProgress = tasks.some(
    (task) => task.status === StatusEnum.IN_PROGRESS
  );
  if (hasInProgress) return StatusEnum.IN_PROGRESS;

  // Par défaut, le job est en attente
  return StatusEnum.PENDING;
}

/**
 * Met à jour la tâche d'un job à partir d'un message WebSocket
 * @param jobs Liste des jobs actuels
 * @param message Message reçu via WebSocket
 * @returns Nouvelle liste de jobs avec la tâche mise à jour
 */
export function updateJobsFromWsMessage(
  jobs: Job[],
  message: WsUpdateTaskMessage
): Job[] {
  return jobs.map((job: Job) => {
    if (job.id !== message.job) return job;

    const jobData: JobModel = job.data;
    // Vérifier si la tâche existe déjà
    const existingTask: boolean =
      jobData.tasks.findIndex((task) => task.id === message.id) !== -1;
    let updatedTasks: TaskModel[];
    if (existingTask) {
      // Mise à jour d'une tâche existante
      updatedTasks = jobData.tasks.map((task: TaskModel) => {
        if (task.id !== message.id) return task;
        return {
          ...task,
          status: message.status,
          type: message.type,
          message: message.message,
          progress: message.progress,
          updated_at:
            message.updated_at instanceof Date
              ? message.updated_at
              : message.updated_at
              ? new Date(message.updated_at)
              : null,
          created_at:
            message.created_at instanceof Date
              ? message.created_at
              : message.created_at
              ? new Date(message.created_at)
              : task.created_at,
        };
      });
    } else {
      // Ajout d'une nouvelle tâche

      const newTask: TaskModel = {
        id: message.id,
        status: message.status,
        type: message.type,
        job: message.job,
        message: message.message,
        progress: message.progress,
        updated_at:
          message.updated_at instanceof Date
            ? message.updated_at
            : message.updated_at
            ? new Date(message.updated_at)
            : null,
        created_at:
          message.created_at instanceof Date
            ? message.created_at
            : message.created_at
            ? new Date(message.created_at)
            : null,
        deleted_at: null,
      };
      updatedTasks = [...jobData.tasks, newTask];
    }

    // Trier les tâches par type
    const sortedTasks = [...updatedTasks].sort(
      (a: TaskModel, b: TaskModel) => a.type - b.type
    );
    // Calculer le nouveau statut du job en fonction des tâches
    const newJobStatus = calculateJobStatus(sortedTasks);
    return new Job({
      ...jobData,
      status: newJobStatus,
      tasks: sortedTasks,
    });
  });
}
